import { Button, Flex, HStack, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import Step from 'app/components/UI/Step';
import useStep from 'app/hooks/useStep';
import useAuthContext from 'app/hooks/useAuthContext';
import useFetchCompany from 'app/hooks/useFetchCompany';
import {
  ArrowLeftOutline,
  ArrowRightOutline,
  CheckOutline,
  PointsTransactionReason,
  ScheduledGift,
  useToast,
} from '@aslanpay/portal-common-lib';
import useFetchPots from 'app/hooks/useFetchPots';
import useFetchCompanyConfiguration from 'app/hooks/useFetchCompanyConfiguration';
import { companyPotQueryParams } from 'app/hooks/usePots';
import { bulkRewardEmployeesFormSchema } from 'app/utils/schemas';
import { IndividualRow } from 'app/components/UI/Table/Table.types';
import usePoints from 'app/hooks/usePoints';
import useScheduledGifts, { PRESELECTED_RECIPIENTS_LIMIT } from 'app/hooks/useScheduledGifts';
import { useRewardFlowStore } from 'app/store';
import formatNumber from 'app/utils/formatting';
import {
  formatScheduledAt,
  fromScheduledAt,
  getRejectionMessage,
  isSlotInFuture,
  SCHEDULED_GIFT_REASON,
  toScheduledAt,
} from 'app/utils/scheduledGifts';

import { getGiftingPath, getScheduledGiftsPath } from 'app/utils/routes';

import RewardSelectEmployeesForm from './RewardSelectEmployeesForm';
import RewardDetailsForm from './RewardDetailsForm';
import RewardConfirmAndSendForm from './RewardConfirmAndSendForm';

import { RewardFormFields } from './types';

const steps = [
  { id: 'select-employees', name: 'Select Employee(s)' },
  { id: 'reward-details', name: 'Reward Points' },
  { id: 'confirm-send', name: 'Confirm Data' },
];

const DETAILS_STEP = 1;

function RewardEmployeesStepper() {
  // const submitButtonRef = useRef<HTMLElement>(null);
  const [employeeData, setEmployeeData] = useState<IndividualRow[]>([]);

  const navigate = useNavigate();
  const { companyId } = useParams();
  const { user } = useAuthContext();
  const { data: company } = useFetchCompany(companyId || user?.companyId);
  const toast = useToast();

  // Editing an existing scheduled gift: the flow opens with the gift's employees ticked and its details filled in.
  const [searchParams] = useSearchParams();
  const editGiftId = searchParams.get('editGiftId') || undefined;
  const isEditing = !!editGiftId;
  const { data: giftToEdit, isError: isGiftToEditError } =
    useScheduledGifts.useFetchScheduledGift(editGiftId);
  // A gift can only be edited under its own company's route, and only before it starts sending.
  const isGiftFromAnotherCompany = !!giftToEdit && !!company && giftToEdit.companyId !== company.id;
  const isGiftEditable =
    giftToEdit?.status === 'SCHEDULED' && !!company && giftToEdit.companyId === company.id;

  const { data: companyConfiguration } = useFetchCompanyConfiguration(company?.id);
  const canSchedule = companyConfiguration?.modulesConfig?.modules?.GIFTING === 'ENABLED';
  const canPostAsRecognition =
    companyConfiguration?.modulesConfig?.modules?.REWARD_AND_RECOGNITION === 'ENABLED';

  // The same gift sent again (e.g. after a timeout) reuses its key, so it can't be scheduled twice; a gift whose
  // details changed since the last attempt is a different gift and gets a new key.
  const lastScheduledAttempt = useRef<{ snapshot: string; key: string }>();
  const getIdempotencyKey = (snapshot: string) => {
    if (lastScheduledAttempt.current?.snapshot !== snapshot) {
      lastScheduledAttempt.current = { snapshot, key: crypto.randomUUID() };
    }
    return lastScheduledAttempt.current.key;
  };

  // A gift being sent again: its employees and settings, kept from the first render since the store is cleared
  // as soon as the page opens.
  const { preSelectedEmployeeIds, preFilledGift, resetRewardFlowData } = useRewardFlowStore();
  const [giftToSendAgain] = useState(preFilledGift);

  const { currentStep, nextStep, previousStep, canPrevStep, setStep } = useStep(steps.length);

  const { data: pots } = useFetchPots(companyPotQueryParams(company?.id));

  const pot = useMemo(() => pots?.list?.[0], [pots]);
  const pointsAmount = useMemo(() => pot?.balance || 0, [pot]);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>(preSelectedEmployeeIds);

  const {
    register,
    watch,
    control,
    formState: { errors, isValid },
    setValue,
    trigger,
  } = useForm<RewardFormFields>({
    resolver: yupResolver(bulkRewardEmployeesFormSchema),
    mode: 'onBlur',
    context: {
      availablePoints: pointsAmount,
      selectedEmployeeCount: selectedEmployeeIds.length,
    }, // ✅ move context here
    defaultValues: {
      pointsAmount: giftToSendAgain?.pointsAmount,
      transactionReason: giftToSendAgain?.transactionReason,
      customMessage: giftToSendAgain?.notes,
      sendMode: 'NOW',
      scheduleDate: '',
      scheduleTime: '',
      postAsRecognition: !!giftToSendAgain?.postAsRecognition,
    },
  });

  const watchedSendMode = watch('sendMode');
  const watchedPostAsRecognition = !!watch('postAsRecognition') && canPostAsRecognition;
  const isScheduling = isEditing || watchedSendMode === 'SCHEDULE';

  useEffect(() => {
    resetRewardFlowData();

    return () => {
      resetRewardFlowData();
    };
  }, [resetRewardFlowData]);

  // Fill in the gift being edited once it has loaded.
  const hasPreFilledEdit = useRef(false);
  useEffect(() => {
    if (giftToEdit && isGiftEditable && !hasPreFilledEdit.current) {
      const { date, time } = fromScheduledAt(giftToEdit.scheduledAt);
      setValue('pointsAmount', giftToEdit.pointsAmount, { shouldValidate: true });
      setValue('transactionReason', giftToEdit.transactionReason, { shouldValidate: true });
      setValue('customMessage', giftToEdit.notes ?? '', { shouldValidate: true });
      setValue('postAsRecognition', giftToEdit.postAsRecognition);
      setValue('sendMode', 'SCHEDULE');
      setValue('scheduleDate', date, { shouldValidate: true });
      setValue('scheduleTime', time, { shouldValidate: true });
      hasPreFilledEdit.current = true;
    }
  }, [giftToEdit, isGiftEditable, setValue]);

  // Sending again a gift that was also posted as a recognition: that only applies to a scheduled gift, so start
  // on scheduling rather than silently drop it.
  const hasDefaultedToSchedule = useRef(false);
  useEffect(() => {
    if (canSchedule && giftToSendAgain?.postAsRecognition && !hasDefaultedToSchedule.current) {
      setValue('sendMode', 'SCHEDULE', { shouldValidate: true });
      hasDefaultedToSchedule.current = true;
    }
  }, [canSchedule, giftToSendAgain, setValue]);

  // A gift's employees can be changed while editing, up to a size the browser can hold; larger gifts keep them.
  const isGiftTooLargeToChangeRecipients =
    !!giftToEdit && giftToEdit.recipientCount > PRESELECTED_RECIPIENTS_LIMIT;
  const canLoadEditRecipients = isGiftEditable && !isGiftTooLargeToChangeRecipients;
  const { data: editRecipients, isError: isEditRecipientsError } =
    useScheduledGifts.useFetchGiftRecipientsAsEmployees(giftToEdit, canLoadEditRecipients);
  // Every one of the gift's employees must be on the list to change it; anyone missing (left, or on unpaid leave
  // and so not listed in step 1) would otherwise be silently dropped from the gift.
  const areSomeRecipientsHidden =
    !!giftToEdit && !!editRecipients && editRecipients.length < giftToEdit.recipientCount;
  const originalRecipientIds = useMemo(
    () =>
      editRecipients?.length && !areSomeRecipientsHidden
        ? editRecipients.map((employee) => employee.id)
        : undefined,
    [editRecipients, areSomeRecipientsHidden]
  );
  const canEditRecipients = !!originalRecipientIds;
  const isLoadingEditRecipients =
    canLoadEditRecipients && !editRecipients && !isEditRecipientsError;
  // When the employees can't be changed, the gift keeps them as they are.
  const isEditRecipientsLocked = isGiftEditable && !isLoadingEditRecipients && !canEditRecipients;

  // Tick the edited gift's current employees, so step 1 shows who it is going to.
  useEffect(() => {
    if (originalRecipientIds) {
      setSelectedEmployeeIds(originalRecipientIds);
    }
  }, [originalRecipientIds]);

  // Editing starts on step 1 to change the employees; when they can't change, it starts on the details step.
  useEffect(() => {
    if (isEditRecipientsLocked) {
      setStep(DETAILS_STEP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditRecipientsLocked]);

  const getLockedRecipientsMessage = () => {
    const toChangeThem = 'To change them, cancel this gift and schedule a new one.';
    if (isGiftTooLargeToChangeRecipients) {
      return `The employees stay the same, as this gift is for more than ${formatNumber(
        PRESELECTED_RECIPIENTS_LIMIT
      )} people. ${toChangeThem}`;
    }
    if (isEditRecipientsError) {
      return `We couldn't load this gift's employees, so they stay the same. ${toChangeThem}`;
    }
    return `Some of this gift's employees can't be shown here, as they have left or are on unpaid leave, so the employees stay the same. ${toChangeThem}`;
  };
  const lockedRecipients =
    giftToEdit && isEditRecipientsLocked
      ? { count: giftToEdit.recipientCount, message: getLockedRecipientsMessage() }
      : undefined;
  const employeeCount = lockedRecipients?.count ?? selectedEmployeeIds.length;

  // Worked out when needed rather than on every render, as the gifting path needs the company to have loaded.
  const getScheduledGiftsTabPath = () =>
    companyId ? getScheduledGiftsPath(companyId) : getGiftingPath();
  const getExitPath = () => (isEditing ? getScheduledGiftsTabPath() : getGiftingPath());

  const { mutate: submitBulkReward, isLoading } = usePoints.useRewardPoints({
    onSuccess: (_, variables) => {
      const count = variables.individualIds?.length || 0;
      const points = variables.pointsAmount;
      toast({
        title: `${count} ${count === 1 ? 'employee' : 'employees'} successfully rewarded ${
          points * count
        } points in total`,
        description: `The selected ${
          count === 1 ? 'employee has' : 'employees have'
        } received ${points} points each`,
        status: 'success',
      });
      navigate(`/company-selection/${companyId}/gifting`);
    },
    onError: () => {
      toast({ title: 'Something went wrong', status: 'error' });
    },
  });

  const handleScheduledGiftSaved = (gift: ScheduledGift, title: string) => {
    toast({
      position: 'top-right',
      status: 'success',
      title,
      description: `It will be sent on ${formatScheduledAt(gift.scheduledAt)}.`,
    });
    if (gift.shortfallWarning) {
      toast({
        position: 'top-right',
        status: 'warning',
        title: 'Not enough points yet',
        // What the API measures: this gift, plus the company's other gifts due in the next 24 hours, against the
        // points it has now.
        description: `This gift and the company's other gifts due in the next 24 hours need ${formatNumber(
          gift.shortfallWarning.pointsNeeded
        )} points, but only ${formatNumber(
          gift.shortfallWarning.pointsAvailable
        )} are available now. Top up before it's sent, or employees won't receive it.`,
      });
    }
    navigate(getScheduledGiftsTabPath());
  };

  const { mutate: createScheduledGift, isLoading: isCreatingScheduledGift } =
    useScheduledGifts.useCreateScheduledGift({
      onSuccess: (gift) => handleScheduledGiftSaved(gift, 'Gift scheduled'),
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Failed to schedule the gift',
          description:
            getRejectionMessage(error) ??
            'Sorry, we failed to schedule the gift. Please try again.',
        });
      },
    });

  const { mutate: updateScheduledGift, isLoading: isUpdatingScheduledGift } =
    useScheduledGifts.useUpdateScheduledGift({
      onSuccess: (gift) => handleScheduledGiftSaved(gift, 'Scheduled gift updated'),
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Failed to update the gift',
          description:
            getRejectionMessage(error) ??
            'Sorry, we failed to update the gift. It may already be sending. Please try again.',
        });
      },
    });

  // Sends the gift now, schedules it, or saves the changes to the gift being edited.
  const handleConfirm = () => {
    const transactionReason = watch('transactionReason') as PointsTransactionReason;
    const notes = watch('customMessage');
    const scheduleDate = watch('scheduleDate') || '';
    const scheduleTime = watch('scheduleTime') || '';
    const scheduledAt = toScheduledAt(scheduleDate, scheduleTime) || '';
    // Carried over from the gift being sent again; back office has no label picker of its own.
    const labelId = giftToSendAgain?.labelId;

    // The time may have passed while the admin was on this step; the API would only refuse it, so go back to
    // pick another.
    if (isScheduling && !isSlotInFuture(scheduleDate, scheduleTime)) {
      toast({
        position: 'top-right',
        status: 'warning',
        title: 'The send time has passed',
        description: 'Please pick a time in the future.',
      });
      setStep(DETAILS_STEP);
      trigger(['scheduleDate', 'scheduleTime']);
      return;
    }

    if (giftToEdit) {
      // Send the employees only when they changed, since the list replaces the gift's whole list.
      const haveRecipientsChanged =
        !!originalRecipientIds &&
        (selectedEmployeeIds.length !== originalRecipientIds.length ||
          selectedEmployeeIds.some((id) => !originalRecipientIds.includes(id)));
      updateScheduledGift({
        id: giftToEdit.id,
        body: {
          individualIds: haveRecipientsChanged ? selectedEmployeeIds : undefined,
          pointsAmount: Number(watch('pointsAmount')),
          notes,
          scheduledAt,
          // Without Reward & Recognition the option isn't shown, so leave the gift's own setting as it is.
          ...(canPostAsRecognition ? { postAsRecognition: !!watch('postAsRecognition') } : {}),
        },
      });
    } else if (watchedSendMode === 'SCHEDULE') {
      const gift = {
        companyId: company?.id || '',
        individualIds: selectedEmployeeIds,
        // The only reason the API schedules; the form already shows it.
        transactionReason: SCHEDULED_GIFT_REASON,
        pointsAmount: Number(watch('pointsAmount')),
        notes,
        labelId,
        scheduledAt,
        postAsRecognition: watchedPostAsRecognition,
      };
      const snapshot = JSON.stringify({
        ...gift,
        individualIds: [...gift.individualIds].sort(),
      });
      createScheduledGift({ ...gift, idempotencyKey: getIdempotencyKey(snapshot) });
    } else {
      submitBulkReward({
        individualIds: selectedEmployeeIds,
        companyId: company?.id || '',
        transactionReason,
        pointsAmount: watch('pointsAmount'),
        notes,
        labelId,
      });
    }
  };

  const handleBack = () => {
    // When an edited gift's employees can't change, there's no step 1 to go back to.
    if (currentStep === 0 || (isEditing && currentStep === DETAILS_STEP && !canEditRecipients)) {
      navigate(getExitPath());
    } else {
      previousStep();
    }
  };

  const renderCurrentStepForm = () => {
    switch (steps[currentStep].id) {
      case 'select-employees':
        return (
          <RewardSelectEmployeesForm
            selectedEmployeeIds={selectedEmployeeIds}
            setSelectedEmployeeIds={setSelectedEmployeeIds}
            onStepAction={nextStep}
            setEmployeeData={setEmployeeData}
          />
        );
      case 'reward-details':
        return (
          <RewardDetailsForm
            register={register}
            errors={errors}
            selectedEmployeeCount={employeeCount}
            onStepAction={nextStep}
            currentPoints={pointsAmount || 0}
            watch={watch}
            control={control}
            setValue={setValue}
            trigger={trigger}
            canSchedule={canSchedule}
            canPostAsRecognition={canPostAsRecognition}
            isEditing={isEditing}
          />
        );
      case 'confirm-send':
        return (
          <RewardConfirmAndSendForm
            selectedEmployeeIds={selectedEmployeeIds}
            watch={watch}
            employeeData={employeeData}
            isScheduling={isScheduling}
            postAsRecognition={watchedPostAsRecognition}
            lockedRecipients={lockedRecipients}
          />
        );
      default:
        return null;
    }
  };

  const pageTitle = isEditing ? 'Edit scheduled gift' : 'Reward employees';
  const isSaving = isLoading || isCreatingScheduledGift || isUpdatingScheduledGift;

  const getConfirmLabel = () => {
    if (isEditing) {
      return 'Save changes';
    }
    if (watchedSendMode === 'SCHEDULE') {
      return 'Schedule gift';
    }
    return 'Confirm';
  };

  const getUnavailableMessage = () => {
    if (isGiftToEditError) {
      return 'Sorry, we could not load this gift. Please try again.';
    }
    if (isGiftFromAnotherCompany) {
      return "This gift belongs to another company, so it can't be changed here.";
    }
    return 'This gift can no longer be changed because it has already been sent or cancelled.';
  };

  // Only this company's gifts that haven't started sending can be edited.
  if (
    isGiftToEditError ||
    isGiftFromAnotherCompany ||
    (giftToEdit && giftToEdit.status !== 'SCHEDULED')
  ) {
    return (
      <AuthenticatedPageWrapper title="Gifting">
        <Flex flexDir="column" gap="20px" align="flex-start" data-testid="edit-gift-unavailable">
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            {pageTitle}
          </Text>
          <Text textStyle="smallRegular" color="brand.darkGray">
            {getUnavailableMessage()}
          </Text>
          <Button variant="outline" onClick={() => navigate(getScheduledGiftsTabPath())}>
            Back to scheduled gifts
          </Button>
        </Flex>
      </AuthenticatedPageWrapper>
    );
  }

  return (
    <AuthenticatedPageWrapper title="Gifting">
      <Flex flexDirection="column" gap="25px">
        <Button
          onClick={handleBack}
          variant="outline"
          color="brand.purple"
          borderColor="white"
          width="fit-content"
          leftIcon={<ArrowLeftOutline boxSize="20px" />}
        >
          Back
        </Button>

        <Flex justifyContent="space-between" alignItems="center">
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            {pageTitle}
          </Text>
          <HStack spacing={4}>
            <Button
              variant={!canPrevStep ? 'outline' : 'link'}
              color="brand.purple"
              borderColor="white"
              onClick={() => navigate(getExitPath())}
            >
              Cancel
            </Button>
            {canPrevStep && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<ArrowLeftOutline boxSize="20px" />}
                onClick={handleBack}
              >
                Back
              </Button>
            )}
            {currentStep === steps.length - 1 ? (
              <Button
                colorScheme="purple"
                size="sm"
                rightIcon={<CheckOutline boxSize="20px" />}
                onClick={handleConfirm}
                isLoading={isSaving}
                data-testid="reward-employees-confirm-button"
              >
                {getConfirmLabel()}
              </Button>
            ) : (
              <Button
                colorScheme="purple"
                rightIcon={<ArrowRightOutline boxSize="20px" />}
                onClick={nextStep}
                size="sm"
                isDisabled={
                  (currentStep === 0 && selectedEmployeeIds.length === 0) ||
                  (currentStep === DETAILS_STEP && !isValid) ||
                  (isEditing && (!isGiftEditable || isLoadingEditRecipients))
                }
                data-testid="reward-employees-next-button"
              >
                Next
              </Button>
            )}
          </HStack>
        </Flex>

        <Flex flexDir="column" gap="50px" height="100%" flexGrow={1}>
          <Flex w="full" maxW="370px" alignSelf="center">
            {steps.map((step, id) => (
              <Step
                key={step.name}
                number={id + 1}
                name={step.name}
                isActive={currentStep === id}
                isCompleted={currentStep > id}
                isFirstStep={id === 0}
                isLastStep={steps.length === id + 1}
              />
            ))}
          </Flex>
          {renderCurrentStepForm()}
        </Flex>
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default RewardEmployeesStepper;
